import { FreelancerListItem } from "@/types/freelancer";
import { Vaga } from "@/types/vaga";
import { Moeda, parseMoeda } from "@/utils/money";

export type MatchStatus = "match" | "parcial" | "mismatch" | "sem_dado";

export interface MatchResult {
  status: MatchStatus;
  detalhe: string;
}

const normalize = (s: string): string =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .trim();

const splitAreas = (s: string | null | undefined): string[] => {
  if (!s) return [];
  return s
    .split(/[,;|/]/)
    .map((p) => p.trim())
    .filter(Boolean);
};

export function matchAreas(vaga: Pick<Vaga, "areas">, f: FreelancerListItem): MatchResult {
  const vagaAreas = splitAreas(vaga.areas).map(normalize);
  const freAreas = (f.areas ?? []).map(normalize);
  if (vagaAreas.length === 0) return { status: "sem_dado", detalhe: "Vaga sem áreas definidas" };
  if (freAreas.length === 0) return { status: "sem_dado", detalhe: "Freelancer sem áreas" };

  const overlap = vagaAreas.filter((a) => freAreas.includes(a));
  if (overlap.length === vagaAreas.length) {
    return { status: "match", detalhe: `Cobre todas: ${vagaAreas.join(", ")}` };
  }
  if (overlap.length > 0) {
    const faltam = vagaAreas.filter((a) => !freAreas.includes(a));
    return {
      status: "parcial",
      detalhe: `Cobre ${overlap.join(", ")}; falta ${faltam.join(", ")}`,
    };
  }
  return { status: "mismatch", detalhe: `Nenhuma área em comum (vaga: ${vagaAreas.join(", ")})` };
}

export function matchLocalizacao(
  vaga: Pick<Vaga, "localizacao_cidade" | "localizacao_estado">,
  f: FreelancerListItem,
): MatchResult {
  const vCidade = vaga.localizacao_cidade ? normalize(vaga.localizacao_cidade) : null;
  const vEstado = vaga.localizacao_estado ? normalize(vaga.localizacao_estado) : null;
  if (!vCidade && !vEstado) return { status: "sem_dado", detalhe: "Vaga sem localização" };

  const fCidade = f.city ? normalize(f.city) : f.cidade ? normalize(f.cidade) : null;
  const fEstado = f.state ? normalize(f.state) : null;
  if (!fCidade && !fEstado) return { status: "sem_dado", detalhe: "Freelancer sem localização" };

  const cidadeOk = vCidade && fCidade && vCidade === fCidade;
  const estadoOk = vEstado && fEstado && vEstado === fEstado;

  if (cidadeOk && (estadoOk || !vEstado)) {
    return { status: "match", detalhe: `Mesma cidade (${f.city ?? f.cidade})` };
  }
  if (estadoOk) {
    return {
      status: "parcial",
      detalhe: `Mesmo estado (${f.state}), cidade diferente${fCidade ? ` (${f.city ?? f.cidade})` : ""}`,
    };
  }
  return {
    status: "mismatch",
    detalhe: `Vaga: ${[vaga.localizacao_cidade, vaga.localizacao_estado].filter(Boolean).join("/") || "—"}; freelancer: ${[f.city ?? f.cidade, f.state].filter(Boolean).join("/") || "—"}`,
  };
}

/**
 * Tenta extrair um valor (ou range) em reais a partir do texto livre de pretensão.
 * Aceita formatos como "5000", "R$ 5.000", "5.000,00", "5k-7k", "5000 a 7000".
 * Retorna [min, max] em reais ou null se não conseguiu interpretar.
 */
export function parsePretensaoReais(raw: string | null | undefined): [number, number] | null {
  if (!raw) return null;
  const cleaned = raw.toLowerCase().replace(/r\$/g, " ").replace(/\s+/g, " ").trim();
  if (!cleaned) return null;

  // Captura números com "k" (mil) — ex: "5k", "5,5k", "5.5k"
  const kMatches = [...cleaned.matchAll(/(\d+(?:[.,]\d+)?)\s*k\b/g)].map((m) =>
    parseFloat(m[1].replace(",", ".")) * 1000,
  );
  // Captura números "puros" (sem k): pega o token inteiro, normaliza separadores
  // pt-BR ("5.000,50" → 5000.50; "5000" → 5000).
  const numMatches = [...cleaned.matchAll(/(\d{1,3}(?:[.,]\d{3})+(?:[.,]\d{1,2})?|\d+(?:[.,]\d{1,2})?)(?!\s*k)/g)]
    .map((m) => {
      const t = m[1];
      // Se tem ambos . e , — o último separador é o decimal
      if (t.includes(".") && t.includes(",")) {
        const lastDot = t.lastIndexOf(".");
        const lastComma = t.lastIndexOf(",");
        if (lastComma > lastDot) return parseFloat(t.replace(/\./g, "").replace(",", "."));
        return parseFloat(t.replace(/,/g, ""));
      }
      // Só vírgula: provavelmente decimal pt-BR
      if (t.includes(",")) return parseFloat(t.replace(",", "."));
      // Só ponto: pode ser milhar pt-BR (5.000) ou decimal en-US (5.5).
      // Heurística: se 3 dígitos depois do último ponto → milhar.
      if (t.includes(".")) {
        const parts = t.split(".");
        if (parts[parts.length - 1].length === 3) return parseFloat(t.replace(/\./g, ""));
        return parseFloat(t);
      }
      return parseFloat(t);
    })
    .filter((n) => Number.isFinite(n) && n > 0);

  const all = [...kMatches, ...numMatches].filter((n) => n > 0);
  if (all.length === 0) return null;
  const min = Math.min(...all);
  const max = Math.max(...all);
  return [min, max];
}

/** Pretensão é sempre em reais: verba em dólar compara pela cotação de `cotacaoUsd`. */
export function matchVerba(
  vaga: Pick<Vaga, "verba_minima" | "verba_maxima" | "moeda">,
  f: FreelancerListItem,
  cotacaoUsd?: number,
): MatchResult {
  const vMinCentavos = vaga.verba_minima ?? null;
  const vMaxCentavos = vaga.verba_maxima ?? null;
  if (vMinCentavos == null && vMaxCentavos == null) {
    return { status: "sem_dado", detalhe: "Vaga sem faixa de verba" };
  }
  const parsed = parsePretensaoReais(f.pretensao_salarial);
  if (!parsed) return { status: "sem_dado", detalhe: "Sem pretensão registrada" };

  const emDolar = parseMoeda(vaga.moeda) === Moeda.USD;
  if (emDolar && !cotacaoUsd) return { status: "sem_dado", detalhe: "Sem cotação do dólar" };
  const cotacao = emDolar ? cotacaoUsd! : 1;

  const [fMin, fMax] = parsed;
  const vMin = vMinCentavos != null ? (vMinCentavos / 100) * cotacao : null;
  const vMax = vMaxCentavos != null ? (vMaxCentavos / 100) * cotacao : null;

  // Freelancer todo abaixo do mínimo da vaga → opcionalmente bom (pede menos),
  // mas para fim de match consideramos como "abaixo" (sinal informativo).
  if (vMin != null && fMax < vMin) {
    return {
      status: "parcial",
      detalhe: `Pretensão abaixo do mínimo (R$${fMax.toLocaleString("pt-BR")} < R$${vMin.toLocaleString("pt-BR")})`,
    };
  }
  if (vMax != null && fMin > vMax) {
    return {
      status: "mismatch",
      detalhe: `Pretensão acima do máximo (R$${fMin.toLocaleString("pt-BR")} > R$${vMax.toLocaleString("pt-BR")})`,
    };
  }
  // Há sobreposição entre [fMin,fMax] e [vMin,vMax]
  return {
    status: "match",
    detalhe: `Dentro da faixa (R$${fMin.toLocaleString("pt-BR")}${fMax !== fMin ? `–${fMax.toLocaleString("pt-BR")}` : ""})`,
  };
}

const STATUS_WEIGHT: Record<MatchStatus, number> = {
  match: 1,
  parcial: 0.5,
  mismatch: 0,
  sem_dado: 0,
};

/**
 * Score consolidado de match (0-1) ponderando áreas (3), localização (2), verba (2).
 * Ignora dimensões sem dado da vaga (não conta peso). Útil para ranking.
 */
export function scoreMatch(vaga: Vaga, f: FreelancerListItem, cotacaoUsd?: number): number {
  const dims: Array<[MatchResult, number]> = [
    [matchAreas(vaga, f), 3],
    [matchLocalizacao(vaga, f), 2],
    [matchVerba(vaga, f, cotacaoUsd), 2],
  ];
  let total = 0;
  let pesoTotal = 0;
  for (const [r, peso] of dims) {
    if (r.status === "sem_dado") continue;
    total += STATUS_WEIGHT[r.status] * peso;
    pesoTotal += peso;
  }
  if (pesoTotal === 0) return 0;
  return total / pesoTotal;
}
